import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CompassIcon, HeartIcon, LayersIcon, ListIcon, MapIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SwipeDeck } from '../../components/swipe/SwipeDeck';
import { EventCardFace } from '../../components/swipe/EventCardFace';
import { EventListRow } from '../../components/performer/EventListRow';
import { EventDetail } from '../../components/EventDetail';
import { EventsMap } from '../../components/EventsMap';
import { FilterSheet } from '../../components/FilterSheet';
import { ProSheet } from '../../components/ProSheet';
import { Sheet } from '../../components/ui/Sheet';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { FREE_APPLY_LIMIT } from '../../hooks/useMarketplaceState';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { activeFilterCount, defaultFilters, matchesFilters, maxSlotBudget } from '../../utils/event';
import { eventFit } from '../../utils/fit';
import { baht } from '../../utils/format';
import { spring } from '../../utils/motion';
import type { EventFilters, MusicEvent } from '../../types/marketplace';
type View = 'swipe' | 'list' | 'map';
type Sort = 'fit' | 'date' | 'budget';
export function Discover() {
  const m = useMarketplace();
  const {
    events,
    me,
    performerPrefs,
    filters,
    setFilters,
    applied,
    passedEvents,
    saved,
    isPro,
    appliesUsed
  } = m;
  const isDesktop = useIsDesktop();
  const [view, setView] = useState<View>('swipe');
  const [sort, setSort] = useState<Sort>('fit');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [panelId, setPanelId] = useState<string | null>(null);
  const [proReason, setProReason] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(t);
  }, []);
  const scored = useMemo(() => events.filter(e => e.status === 'live' && !applied.includes(e.id) && !m.hasMatch(e.id, me.id) && !e.slots.some(s => s.filledBy === me.id)).map(event => ({
    event,
    fit: eventFit(event, performerPrefs, me)
  })), [events, applied, me, performerPrefs, m]);
  const filtered = scored.filter(x => matchesFilters(x.event, filters));
  const deck = filtered.filter(x => !passedEvents.includes(x.event.id) && !saved.includes(x.event.id)).sort((a, b) => Number(b.event.featured) - Number(a.event.featured) || b.fit.score - a.fit.score);
  const list = [...filtered].sort((a, b) => sort === 'fit' ? b.fit.score - a.fit.score : sort === 'date' ? a.event.date.localeCompare(b.event.date) : maxSlotBudget(b.event) - maxSlotBudget(a.event));
  const countFor = (f: EventFilters) => scored.filter(x => matchesFilters(x.event, f)).length;
  const filterCount = activeFilterCount(filters);
  const desktopPanelId = view === 'swipe' ? deck[0]?.event.id : view === 'list' ? panelId ?? list[0]?.event.id : panelId;
  const panelEvent = events.find(e => e.id === desktopPanelId);
  const sheetEvent = events.find(e => e.id === sheetId);
  const open = (id: string) => isDesktop ? setPanelId(id) : setSheetId(id);
  const onLimit = () => {
    setSheetId(null);
    setProReason(`You’ve used all ${FREE_APPLY_LIMIT} free applications this week.`);
  };
  const onSwipe = (event: MusicEvent, dir: 'left' | 'right' | 'up') => {
    if (dir === 'right') {
      const result = m.applyToEvent(event.id);
      if (result === 'limit') {
        setNonce(n => n + 1);
        onLimit();
      } else if (result === 'applied') {
        toast.success(`Applied to ${event.name}`);
      }
    } else if (dir === 'left') {
      m.passEvent(event.id);
    } else {
      m.toggleSave(event.id);
      toast(`Saved ${event.name}`, {
        description: 'Find it in List view.'
      });
    }
  };
  const chips = buildChips(filters, setFilters);
  return <div className="flex h-[calc(100dvh-76px)] flex-col lg:h-[100dvh]" data-id="element-1144" data-mp="i1qalht2smyq">
      <header className="px-5 pb-3 pt-6 lg:px-8 lg:pt-8" data-id="element-1145" data-mp="d3x1afwh7cu8">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1146" data-mp="85dksv0nit99">
          <div data-id="element-1147" data-mp="87gfhscr1akb">
            <h1 className="text-[28px] font-bold leading-tight tracking-[-0.015em]" data-id="element-1148" data-mp="36sh2lm6xrg2">Discover</h1>
            <p className="text-sm text-ink-3" data-id="element-1149" data-mp="22jeg55wzsy4l">
              <span className="tabular font-semibold text-ink-2" data-id="element-1150" data-mp="22j4o7cse0gw9">{filtered.length}</span> events for you
              {!isPro && <>
                  {' · '}
                  <button type="button" onClick={() => setProReason('Apply as often as you like with Pro.')} className="font-semibold text-brand-strong underline-offset-2 hover:underline" data-id="element-1151" data-mp="260iym86fqeap">
                    {Math.max(0, FREE_APPLY_LIMIT - appliesUsed)} free applies left
                  </button>
                </>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1152" data-mp="1n2pwh83my8ed">
            <SegmentedControl<View> ariaLabel="View" layoutId="discover-view" value={view} onChange={setView} options={[{
            value: 'swipe',
            label: 'Swipe',
            icon: <LayersIcon className="h-4 w-4" data-id="element-1154" data-mp="1w5qzgzx7fjdt" />
          }, {
            value: 'list',
            label: 'List',
            icon: <ListIcon className="h-4 w-4" data-id="element-1155" data-mp="210ei4i5j1jkh" />
          }, {
            value: 'map',
            label: 'Map',
            icon: <MapIcon className="h-4 w-4" data-id="element-1156" data-mp="37ibdyhpyn0i5" />
          }]} data-id="element-1153" data-mp="1n4ap2s6e3h9l" />
            <motion.button type="button" onClick={() => setFiltersOpen(true)} whileTap={{
            scale: 0.92
          }} transition={spring.snappy} aria-label={`Filters${filterCount ? `, ${filterCount} active` : ''}`} className="relative grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-ink transition-colors duration-150 hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1157" data-mp="3cgcsqqgcsdnt">
              <SlidersHorizontalIcon className="h-5 w-5" data-id="element-1158" data-mp="240ws5sf67eq2" />
              {filterCount > 0 && <motion.span key={filterCount} initial={{
              scale: 0.5
            }} animate={{
              scale: 1
            }} transition={spring.bouncy} className="tabular absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-on-brand ring-2 ring-bg" data-id="element-1159" data-mp="1z1tlk3jjl0fz">
                  {filterCount}
                </motion.span>}
            </motion.button>
          </div>
        </div>
        {chips.length > 0 && <div className="no-scrollbar -mx-5 mt-3 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:px-0" data-id="element-1160" data-mp="n5vtwv1wtel">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-1161" data-mp="3i3nfuch5gmua">
              {chips.map(c => <motion.button layout key={c.key} type="button" onClick={c.remove} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={spring.snappy} aria-label={`Remove filter ${c.label}`} className="flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-ink pl-3 pr-2 text-[13px] font-bold text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1162" data-mp="3n2qm208v8z3u">
                  {c.label}
                  <XIcon className="h-3.5 w-3.5" data-id="element-1163" data-mp="3m14a1kj0bpwi" />
                </motion.button>)}
            </AnimatePresence>
          </div>}
      </header>

      <div className="flex min-h-0 flex-1" data-id="element-1164" data-mp="3h3ul05zj5g97">
        <div className="relative min-w-0 flex-1" data-id="element-1165" data-mp="3d0xyi46vczzz">
          {loading ? <div className="mx-auto flex h-full max-w-[420px] flex-col px-5 pb-6" data-id="element-1166" data-mp="3813801r0acfg">
              <Skeleton className="flex-1 rounded-lg" data-id="element-1167" data-mp="8lopnmuqmajb" />
              <div className="mt-5 flex justify-center gap-4" data-id="element-1168" data-mp="8keryyrsr4d7">
                <Skeleton className="h-14 w-14 rounded-full" data-id="element-1169" data-mp="4jl23zusnfho" />
                <Skeleton className="h-16 w-16 rounded-full" data-id="element-1170" data-mp="4m7gojg3qv9e" />
              </div>
            </div> : view === 'swipe' ? <div className="h-full px-5 pb-6 pt-2" data-id="element-1171" data-mp="3r07eb2r5xs3f">
              <SwipeDeck items={deck} getKey={x => `${x.event.id}-${nonce}`} renderCard={x => <EventCardFace event={x.event} fit={x.fit} onDetails={() => open(x.event.id)} data-id="element-1173" data-mp="niywgiwfqs8p" />} onSwipe={(x, dir) => onSwipe(x.event, dir)} stamps={{
            right: 'Apply',
            left: 'Pass',
            up: 'Save'
          }} allowUp likeLabel="Apply" likeIcon={<HeartIcon className="h-7 w-7" fill="currentColor" data-id="element-1174" data-mp="14sb16izjcgz9" />} onUndo={() => m.undoPass()} canUndo={passedEvents.length > 0} empty={<div className="grid h-full place-items-center rounded-lg border-2 border-dashed border-line" data-id="element-1175" data-mp="18rjujmqnycua">
                    <EmptyState icon={<CompassIcon className="h-7 w-7" data-id="element-1177" data-mp="1iqrhn1n79lul" />} title="You’ve seen everything nearby" body="Widen your filters or tweak your preferences to see more events." data-id="element-1176" data-mp="1irsth8c1icdd">
                      <Button variant="primary" onClick={() => setFiltersOpen(true)} data-id="element-1178" data-mp="1sjyh65v9e7mr">
                        Widen filters
                      </Button>
                      <Link to="/p/profile#preferences" className="inline-flex h-11 items-center rounded-full px-5 font-bold text-ink hover:bg-surface-2" data-id="element-1179" data-mp="21jln94yxo1m5">
                        Preferences
                      </Link>
                    </EmptyState>
                  </div>} data-id="element-1172" data-mp="3qzfjvv9ay0eq" />
            </div> : view === 'list' ? <div className="h-full overflow-y-auto px-3 pb-6 lg:px-6" data-id="element-1180" data-mp="gd53npr59r6x">
              <div className="flex items-center justify-between px-2 pb-2" data-id="element-1181" data-mp="geg6nncrmuwz">
                <p className="text-sm text-ink-3" data-id="element-1182" data-mp="kdxdak3xhy3q">Swipe a row left to save</p>
                <SegmentedControl<Sort> ariaLabel="Sort" layoutId="discover-sort" size="sm" value={sort} onChange={setSort} options={[{
              value: 'fit',
              label: 'Best fit'
            }, {
              value: 'date',
              label: 'Soonest'
            }, {
              value: 'budget',
              label: 'Budget'
            }]} data-id="element-1183" data-mp="pd0kaa3gyeec" />
              </div>
              {list.length === 0 ? <EmptyState icon={<ListIcon className="h-7 w-7" data-id="element-1185" data-mp="1i9sylf4hecn0" />} title="No events match" body="Try removing a filter or two." data-id="element-1184" data-mp="1i99r6scgj6n8">
                  <Button variant="primary" onClick={() => setFilters(defaultFilters)} data-id="element-1186" data-mp="1dg8b4h0il122">
                    Clear filters
                  </Button>
                </EmptyState> : <ul className="space-y-1" data-id="element-1187" data-mp="14io7j1f6isoa">
                  {list.map(x => <EventListRow key={x.event.id} event={x.event} fit={x.fit} saved={saved.includes(x.event.id)} active={isDesktop && desktopPanelId === x.event.id} onOpen={() => open(x.event.id)} onToggleSave={() => {
              const now = m.toggleSave(x.event.id);
              toast(now ? `Saved ${x.event.name}` : `Removed from saved`);
            }} data-id="element-1188" data-mp="215swt7o1ycf1" />)}
                </ul>}
            </div> : <div className="relative isolate h-full overflow-hidden lg:rounded-tl-lg" data-id="element-1189" data-mp="i85ys7cyxu8w">
              <EventsMap events={list.map(x => x.event)} selectedId={isDesktop ? panelId : sheetId ?? panelId} onSelect={id => setPanelId(id)} data-id="element-1190" data-mp="48n68mwn4rhp" />
              <AnimatePresence data-id="element-1191" data-mp="96evf95whzi7">
                {!isDesktop && panelId && (() => {
              const sel = list.find(x => x.event.id === panelId);
              if (!sel) return null;
              return <motion.button key={panelId} type="button" onClick={() => setSheetId(panelId)} initial={{
                y: 140,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} exit={{
                y: 140,
                opacity: 0
              }} transition={spring.smooth} className="absolute inset-x-3 bottom-3 z-[500] flex items-center gap-3 rounded-md bg-surface p-3 text-left shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1192" data-mp="3vh0yv7igbtfv">
                      <img src={sel.event.photos[0]} alt="" className="h-20 w-20 shrink-0 rounded-sm object-cover" data-id="element-1193" data-mp="2o5y4gyser025" />
                      <span className="min-w-0 flex-1" data-id="element-1194" data-mp="2y2149xzsemlx">
                        <span className="block truncate font-bold" data-id="element-1195" data-mp="2y0g8k51qkxmh">{sel.event.name}</span>
                        <span className="block truncate text-sm text-ink-2" data-id="element-1196" data-mp="3bzy20xpz8z26">
                          {sel.event.city.split(',')[0]} · {sel.event.distanceKm} km
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-sm" data-id="element-1197" data-mp="3lq0umwt6sazz">
                          <span className="tabular font-bold" data-id="element-1198" data-mp="3grzas9nxs7ng">up to {baht(maxSlotBudget(sel.event))}</span>
                          <span className="tabular rounded-full bg-surface-2 px-2 text-[12px] font-bold leading-5" data-id="element-1199" data-mp="3ut1x8niufjyw">{sel.fit.score}% fit</span>
                        </span>
                      </span>
                    </motion.button>;
            })()}
              </AnimatePresence>
            </div>}
        </div>

        <aside className="hidden w-[440px] shrink-0 overflow-y-auto border-l border-line px-5 pb-6 pt-1 lg:block" data-id="element-1200" data-mp="1t0hzoorr68r7">
          {panelEvent ? <EventDetail key={panelEvent.id} event={panelEvent} onLimit={onLimit} data-id="element-1201" data-mp="22sxl5w29dfgu" /> : <EmptyState icon={<MapIcon className="h-7 w-7" data-id="element-1203" data-mp="15674vn6wduyw" />} title="Pick an event" body="Select a pin or a row to see the full brief." data-id="element-1202" data-mp="155oyvyufo2ds" />}
        </aside>
      </div>

      <Sheet open={Boolean(sheetEvent)} onClose={() => setSheetId(null)} title={sheetEvent?.name ?? 'Event'} hideTitle tall data-id="element-1204" data-mp="1f3biuzqlak4m">
        {sheetEvent && <EventDetail key={sheetEvent.id} event={sheetEvent} onLimit={onLimit} onDone={() => setSheetId(null)} data-id="element-1205" data-mp="1j5geu54ifsuf" />}
      </Sheet>
      <FilterSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} filters={filters} onApply={setFilters} countFor={countFor} data-id="element-1206" data-mp="2qkb9zs17asx2" />
      <ProSheet open={proReason !== null} onClose={() => setProReason(null)} reason={proReason ?? undefined} data-id="element-1207" data-mp="ix02n0pioieb" />
    </div>;
}
function buildChips(f: EventFilters, set: (f: EventFilters) => void) {
  const chips: {
    key: string;
    label: string;
    remove: () => void;
  }[] = [];
  const dateLabels = {
    week: 'This week',
    month: 'This month',
    quarter: 'Next 3 months',
    any: ''
  };
  if (f.dateRange !== 'any') chips.push({
    key: 'date',
    label: dateLabels[f.dateRange],
    remove: () => set({
      ...f,
      dateRange: 'any'
    })
  });
  if (f.maxDistance < defaultFilters.maxDistance) chips.push({
    key: 'dist',
    label: `≤ ${f.maxDistance} km`,
    remove: () => set({
      ...f,
      maxDistance: defaultFilters.maxDistance
    })
  });
  if (f.budget[0] > 0 || f.budget[1] < defaultFilters.budget[1]) chips.push({
    key: 'budget',
    label: `${baht(f.budget[0])}–${baht(f.budget[1])}`,
    remove: () => set({
      ...f,
      budget: defaultFilters.budget
    })
  });
  f.types.forEach(t => chips.push({
    key: `t-${t}`,
    label: t,
    remove: () => set({
      ...f,
      types: f.types.filter(x => x !== t)
    })
  }));
  f.genres.forEach(g => chips.push({
    key: `g-${g}`,
    label: g,
    remove: () => set({
      ...f,
      genres: f.genres.filter(x => x !== g)
    })
  }));
  if (f.multiActOnly) chips.push({
    key: 'multi',
    label: 'Multi-act',
    remove: () => set({
      ...f,
      multiActOnly: false
    })
  });
  return chips;
}